/**
 * Spanish translations
 */
import type { TranslationKeys } from '../utils/i18n';

export const es: TranslationKeys = {
  controls: {
    maxTime: 'Tiempo Máximo',
    maxTimeMinutes: 'Tiempo Máximo (minutos)',
    travelMode: 'Modo de Viaje',
    speed: 'Velocidad',
    mapStyle: 'Estilo de Mapa',
    language: 'Idioma',
    share: 'Compartir',
    play: 'Reproducir',
    pause: 'Pausar',
    restart: 'Reiniciar',
    loading: 'Cargando...',
    showControls: 'Mostrar Controles',
    hideControls: 'Ocultar Controles',
    expandControls: 'Expandir controles',
    collapseControls: 'Contraer controles',
    selectLocationHint: 'Por favor seleccione una ubicación en el mapa para comenzar',
  },
  travelModes: {
    driving: 'Conducir',
    walking: 'Caminar',
    cycling: 'Ciclismo',
    transit: 'Tránsito',
  },
  speed: {
    verySlow: '0.1x (Muy Lento)',
    slow: '0.25x (Lento)',
    half: '0.5x (Mitad)',
    normal: '1x (Normal)',
    fast: '1.5x (Rápido)',
    veryFast: '2x (Muy Rápido)',
  },
  mapStyles: {
    streets: 'Calles',
    satellite: 'Satélite',
  },
  languages: {
    en: 'English',
    'zh-Hans': '简体中文 (Simplified Chinese)',
    'zh-Hant': '繁體中文 (Traditional Chinese)',
    es: 'Español (Spanish)',
    fr: 'Français (French)',
    de: 'Deutsch (German)',
    ja: '日本語 (Japanese)',
    ko: '한국어 (Korean)',
  },
  addressInput: {
    label: 'Buscar Dirección',
    placeholder: 'Ingrese una dirección para calcular el mapa de tiempo',
    noResults: 'No se encontraron resultados',
    loading: 'Cargando sugerencias',
  },
  error: {
    retry: 'Reintentar',
    dismiss: 'Descartar error',
    somethingWentWrong: 'Algo salió mal. Por favor actualice la página.',
    browserNotSupported: 'Su navegador no es compatible con esta aplicación. Por favor use un navegador moderno.',
    usageLimitReached: 'Ha alcanzado el límite beta de 5 cálculos. ¡Gracias por probarlo! Envíame un DM si estás interesado en este proyecto.',
  },
  progress: {
    minutes: 'min',
  },
  share: {
    urlCopied: '¡URL copiada al portapapeles!',
    shareUrl: 'Compartir URL',
  },
  emailGate: {
    title: 'Iniciar sesión en Mapa de Tiempo Beta',
    subtitle: 'Ingrese su dirección de correo electrónico para recibir un enlace de inicio de sesión',
    emailPlaceholder: 'your.email@example.com',
    emailLabel: 'Dirección de correo electrónico',
    sendButton: 'Enviar Enlace de Inicio de Sesión',
    sendingButton: 'Enviando enlace de inicio de sesión...',
    checkEmailTitle: 'Revise su correo electrónico',
    checkEmailMessage: 'Hemos enviado un enlace de inicio de sesión a {email}',
    checkEmailInstruction: 'Haga clic en el enlace del correo electrónico para continuar al mapa de tiempo.',
    checkEmailHelp: '¿No recibió el correo? Revise su carpeta de spam o intente nuevamente con una dirección de correo diferente.',
    emailRequired: 'Por favor ingrese su dirección de correo electrónico',
    emailInvalid: 'Por favor ingrese una dirección de correo electrónico válida',
    domainRestriction: 'Solo permitimos direcciones de correo electrónico @gmail.com, @outlook.com y @qq.com para acceso beta.',
    domainNotAllowed: 'El acceso beta está actualmente limitado a direcciones de correo de Gmail, Outlook y QQ',
    typoSuggestion: '¿Quiso decir @{domain}?',
    rateLimitExceeded: 'Demasiadas solicitudes. Por favor espere un momento e intente nuevamente.',
    invalidEmailFormat: 'Dirección de correo electrónico inválida. Por favor verifique e intente nuevamente.',
    failedToSend: 'Error al enviar el enlace de inicio de sesión. Por favor intente nuevamente.',
    magicLinkFailed: 'Error al enviar enlace mágico: {error}',
    testMeButton: 'Pruébame',
    bannerText: 'Mapa de Tiempo',
    betaTag: 'Beta',
  },
  testCode: {
    title: 'Ingrese el código de prueba',
    subtitle: '¿Tiene un código de prueba? Ingréselo a continuación para comenzar',
    placeholder: 'Ingrese el código',
    button: 'Continuar con el código',
    haveCodeLink: '¿Tiene un código de prueba?',
    useEmailLink: 'Usar correo electrónico en su lugar',
    invalid: 'Código de prueba inválido',
    expired: 'Este código de prueba ha expirado',
    limitReached: 'Este código de prueba ha alcanzado su límite de uso',
    optionalEmailTitle: 'Guardar su progreso (Opcional)',
    optionalEmailSubtitle: '¿Quiere guardar su trabajo? Ingrese su correo electrónico a continuación (o saltar por ahora)',
    optionalEmailPlaceholder: 'tu.correo@ejemplo.com (opcional)',
    skipButton: 'Saltar por ahora',
    submitEmailButton: 'Continuar',
  },
  usageIndicator: {
    template: '{count} cálculos restantes',
    loading: 'Cargando...',
  },
  disclaimer: {
    loading: 'Cargando...',
    title: 'Aviso Importante',
    content: 'Este es un proyecto beta y experimental. Todos los datos del mapa son proporcionados por Mapbox, un servicio de terceros, y no han sido verificados de forma independiente. Los mapas pueden contener inexactitudes y no deben usarse para decisiones críticas. Los cálculos de tiempo de viaje son solo estimaciones y no han sido verificados en cuanto a precisión. No asumimos ninguna responsabilidad por inexactitudes, errores o consecuencias derivadas del uso de esta aplicación.',
    consent: 'Al continuar usando esta aplicación, usted reconoce y acepta todos los términos y descargos de responsabilidad anteriores.',
  },
  intro: {
    title: 'Descubre Tu Alcance',
    message: 'Visualiza qué tan lejos puedes viajar en un tiempo determinado desde cualquier ubicación.\n\nObserva tu alcance de tiempo de viaje animado en el mapa para tomar mejores decisiones sobre dónde vivir, trabajar o visitar.',
    step1: 'Busca o haz clic en el mapa para establecer tu punto de partida',
    step2: 'El mapa muestra tu alcance de tiempo de viaje con zonas animadas',
    step3: 'Configura tu tiempo de viaje y modo de transporte',
    step4: 'Haz clic en Reproducir para ver cómo se expande tu alcance con el tiempo',
    step5: 'Comparte tu visualización con otros mediante un enlace compartible',
    next: 'Siguiente',
    skip: 'Saltar Tour',
    gotIt: '¡Entendido!',
    startTour: 'Iniciar Tour',
  },
};

